import { ShopifyProduct } from "@/lib/shopify";
import type { Json } from "@/integrations/supabase/types";

/**
 * Convert a Supabase shopify_products row to the ShopifyProduct interface
 * used throughout the frontend. This eliminates the need for Storefront API calls
 * in product listings.
 */

interface DbImage {
  src?: string;
  url?: string;
  alt?: string;
  altText?: string;
}

interface DbVariant {
  id?: string;
  shopify_id?: string;
  title?: string;
  price?: number | string;
  available?: boolean;
  inventory_quantity?: number | null;
  weight?: number | null;
  weight_unit?: string | null;
  grams?: number | null;
  selectedOptions?: { name: string; value: string }[];
}

interface DbOption {
  name?: string;
  values?: string[];
}

export interface DbProductRow {
  shopify_id: string;
  handle: string;
  title: string;
  description: string | null;
  vendor: string | null;
  image_url: string | null;
  images: Json | (string | DbImage)[] | null;
  price: number | null;
  compare_at_price: number | null;
  currency: string | null;
  variants: Json | DbVariant[] | null;
  options: Json | DbOption[] | null;
  available_for_sale: boolean | null;
  tags?: string[] | null;
  product_type?: string | null;
  inventory_quantity?: number | null;
  shopify_created_at?: string | null;
}

/** Standard select columns for product listings */
export const DB_PRODUCT_SELECT =
  "shopify_id, handle, title, description, vendor, image_url, images, price, compare_at_price, currency, variants, options, available_for_sale, tags, product_type, inventory_quantity, shopify_created_at" as const;

export function dbToShopifyProduct(p: DbProductRow): ShopifyProduct {
  const images = (Array.isArray(p.images) ? p.images : []) as (string | DbImage)[];
  const variants = (Array.isArray(p.variants) ? p.variants : []) as DbVariant[];
  const options = (Array.isArray(p.options) ? p.options : []) as DbOption[];

  const imageEdges = images.length > 0
    ? images.slice(0, 5).map((img) => ({
        node: {
          url: typeof img === "string" ? img : img.src || img.url || "",
          altText: (typeof img === "object" ? img.alt || img.altText : null) || p.title,
        },
      }))
    : p.image_url
      ? [{ node: { url: p.image_url, altText: p.title } }]
      : [];

  const variantEdges = variants.length > 0
    ? variants.map((v, idx) => {
        const opts = v.selectedOptions || (v as any).selected_options || [];
        // Reconstruct title from selectedOptions when title is missing/default
        let title = v.title || "Default";
        if ((!v.title || v.title === "Default" || v.title === "Default Title") && opts.length > 0) {
          title = opts.map((o: { name: string; value: string }) => o.value).join(" / ");
        }
        // Fallback: use options array values at this index
        if ((!v.title || v.title === "Default" || v.title === "Default Title") && opts.length === 0 && options.length > 0) {
          const optionValues = options
            .map((o) => o.values?.[idx])
            .filter(Boolean);
          if (optionValues.length > 0) title = optionValues.join(" / ");
        }
        return {
          node: {
            id: String(v.id ? `gid://shopify/ProductVariant/${v.id}` : (v.shopify_id || `gid://shopify/ProductVariant/${p.shopify_id}`)),
            title,
            price: {
              amount: String(v.price || p.price || 0),
              currencyCode: p.currency || "SEK",
            },
          availableForSale: (() => {
            // Source of truth: inventory_quantity > 0. Variantens 'available'-flagga
            // från Shopify kan vara stale (felaktigt false) – så vi prioriterar saldo.
            const vQty = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null;
            if (vQty !== null) return vQty > 0;
            if (typeof v.available === 'boolean') return v.available;
            if (typeof p.inventory_quantity === 'number') return p.inventory_quantity > 0;
            return p.available_for_sale ?? false;
          })(),
            selectedOptions: opts,
            weight: v.weight ?? v.grams ?? undefined,
            weightUnit: v.weight_unit ?? (v.grams != null ? "GRAMS" : undefined),
          },
        };
      })
    : [
        {
          node: {
            id: `gid://shopify/ProductVariant/${p.shopify_id}`,
            title: "Default",
            price: {
              amount: String(p.price || 0),
              currencyCode: p.currency || "SEK",
            },
            availableForSale: p.available_for_sale ?? false,
            selectedOptions: [],
          },
        },
      ];

  return {
    node: {
      id: p.shopify_id.startsWith("gid://") ? p.shopify_id : `gid://shopify/Product/${p.shopify_id}`,
      title: p.title,
      description: p.description || "",
      handle: p.handle,
      vendor: p.vendor || undefined,
      inventoryQuantity: p.inventory_quantity ?? null,
      priceRange: {
        minVariantPrice: {
          amount: String(p.price || 0),
          currencyCode: p.currency || "SEK",
        },
      },
      ...(p.compare_at_price
        ? {
            compareAtPriceRange: {
              maxVariantPrice: {
                amount: String(p.compare_at_price),
                currencyCode: p.currency || "SEK",
              },
            },
          }
        : {}),
      images: { edges: imageEdges },
      variants: { edges: variantEdges },
      options: options.map((o) => ({
        name: o.name || "",
        values: o.values || [],
      })),
      ...(p.shopify_created_at ? { shopifyCreatedAt: p.shopify_created_at } : {}),
    },
  };
}
